/**
 * Shared note text for Mentors event registration activity_logs.
 * Mirrored in supabase/functions/_shared/pmEventRegistrationNotes.ts
 * (Deno cannot import from src/). Keep both in sync.
 */

export function formatPmRegistrationTimestamp(iso, timeZone = 'America/New_York') {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timeZone || 'America/New_York',
      timeZoneName: 'short',
    }).format(date);
  } catch {
    try {
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York',
        timeZoneName: 'short',
      }).format(date);
    } catch {
      return null;
    }
  }
}

/**
 * @param {object} ctx
 * @param {string} ctx.eventTitle
 * @param {string|null} [ctx.inviteSlug]
 * @param {string|null} [ctx.inviterName]
 * @param {object|null} [ctx.session]
 * @param {string} [ctx.timezone]
 * @param {string} [ctx.ownerSource] invite_agent | invite_student_sponsor | event_agent | marketing_director | none
 * @param {string|null} [ctx.studentName]
 * @param {string|null} [ctx.sponsorName]
 * @param {string|null} [ctx.marketingDirectorName]
 * @param {string|null} [ctx.businessLaunchAssigneeName]
 * @param {string|null} [ctx.placeholderOwnerName]
 * @param {boolean} [ctx.isDoor]
 * @param {string|null} [ctx.doorStaffName]
 * @param {string} registeredAtIso
 */
export function buildPmEventRegistrationNotes(ctx, registeredAtIso) {
  const timezone = ctx.timezone?.trim() || 'America/New_York';
  const registeredOn = formatPmRegistrationTimestamp(registeredAtIso, timezone) || registeredAtIso;
  const eventTitle = ctx.eventTitle || 'event';
  const eventDate =
    formatPmRegistrationTimestamp(ctx.session?.starts_at, timezone) || 'TBD';
  const sessionTitle = ctx.session?.title?.trim() || 'General';
  const inviteSlug = ctx.inviteSlug || null;
  const ownerSource = ctx.ownerSource || 'none';
  const assigneeName = ctx.businessLaunchAssigneeName?.trim() || null;
  const mdName = ctx.marketingDirectorName?.trim() || null;
  const sponsorName = ctx.sponsorName?.trim() || null;
  const studentName = ctx.studentName?.trim() || null;
  const inviterName = ctx.inviterName?.trim() || null;
  const placeholderOwnerName = ctx.placeholderOwnerName?.trim() || null;

  let body;
  if (
    ownerSource === 'marketing_director'
    && assigneeName
  ) {
    body =
      `Business Launch lead intended for ${assigneeName} (not yet a registered platform user) — ` +
      `routed to Marketing Director ${mdName || 'unassigned'}. ` +
      `Registered for ${eventTitle} on ${registeredOn}.`;
  } else if (ownerSource === 'invite_student_sponsor' && inviteSlug) {
    body =
      `Registered for ${eventTitle} on ${registeredOn} using invitation ${inviteSlug} ` +
      `(student invite — owner is ${sponsorName || 'Unknown'}, sponsor of ${studentName || 'Unknown'}).`;
  } else if (placeholderOwnerName && inviteSlug) {
    body =
      `Registered for ${eventTitle} using invitation link: ${inviteSlug} ` +
      `(invite credited to ${placeholderOwnerName}, not yet a platform user). ` +
      `Registered on ${registeredOn}.`;
  } else if (inviteSlug) {
    body =
      `Registered for ${eventTitle} using invitation link: ${inviteSlug}. ` +
      `Registered on ${registeredOn}. ` +
      `Event date: ${eventDate}. ` +
      `Session: ${sessionTitle}. ` +
      `Inviter: ${inviterName || 'None'}.`;
  } else {
    body =
      `Registered for ${eventTitle} using invitation link: Direct registration (no invitation link). ` +
      `Registered on ${registeredOn}. ` +
      `Event date: ${eventDate}. ` +
      `Session: ${sessionTitle}. ` +
      `Inviter: ${inviterName || 'None'}.`;
  }

  if (ctx.isDoor) {
    const staff = ctx.doorStaffName?.trim() || 'None';
    body = `Door walk-in. ${body} Checked in by: ${staff}.`;
  }

  return body;
}
